/**
 * Approval outcomes in the console (R-HL-3, R-HL-4): which decided request stopped a session,
 * Approve and resume, and Mark as handled. The view models (blockView, approvalEvents,
 * blockedList, resumeDisabledReason) are pure and unit tested; the hooks are thin wrappers over
 * the store. In mock mode the calls change the demo data in place, like decideApproval does.
 */
import { useMemo } from 'react';
import { APPROVAL_BLOCK_LABEL, APPROVAL_SYSTEM_DECIDERS, approvalBlockFor, approvalBlockLead, approvalBlockTitle, approvalOutcomeOf, approvalResumable, approvalsForDerive, MISSION_CLOSED_NOTE, newPrefixedId, redirectWhy } from '@orgus/protocol';
import type { ApprovalBlock, ApprovalBlockLead, ApprovalGrant, ApprovalRedirect, Mission, ApprovalLike, ApprovalRequest, ApprovalResume, ApprovalResumeResponse, ApprovalsState, Id, Session } from '@orgus/protocol';
import { formatClock, useNow } from '@orgus/ui';
import { ApiError, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { useMe } from '../auth/useMe.ts';
import { useApprovals } from './live.ts';
import { decidedText } from './askModel.ts';

const MIN = 60_000;
/** How long a one-shot grant lasts (the hub's GRANT_TTL_MINUTES). */
export const GRANT_MS = 30 * MIN;

// ---------------------------------------------------------------------------
// Pure view models
// ---------------------------------------------------------------------------

/** The command or path a request asked for: subject first, then the input's command or path. */
export function commandOf(a: Pick<ApprovalRequest, 'subject'> & { input?: unknown }): string | null {
  if (a.subject) return a.subject;
  const input = a.input as { command?: unknown; file_path?: unknown; url?: unknown } | undefined;
  if (input && typeof input.command === 'string') return input.command;
  if (input && typeof input.file_path === 'string') return input.file_path;
  if (input && typeof input.url === 'string') return input.url;
  return null;
}

/** Tools whose command text is the whole of what runs: the command shown is the call. */
const SHELL_TOOLS: ReadonlySet<string> = new Set(['bash', 'powershell']);
/** Longer than this, or on more than one line, and a one-line row or a card title clips it. */
export const VISIBLE_COMMAND_MAX = 100;
const PREVIEW_MAX = 4000;

/**
 * Approve and resume asks first (and shows the full call) unless the person can already see
 * everything that will run (SEC-1): high risk always; and any call that is not a shell command
 * (an Edit or Write shows only its path, an MCP call only part of its input) or whose command is
 * long or multi-line, so no mount ever approves text it clipped.
 */
export function resumeNeedsConfirm(a: Pick<ApprovalRequest, 'risk' | 'tool' | 'subject' | 'title'> & { input?: unknown }): boolean {
  if (a.risk === 'high') return true;
  if (!a.tool || !SHELL_TOOLS.has(a.tool.toLowerCase())) return true;
  const cmd = commandOf(a);
  return !cmd || cmd.length > VISIBLE_COMMAND_MAX || cmd.includes('\n');
}

/**
 * Everything the call carries beyond its command or path, for the confirm body: the new text of
 * an Edit or Write, else the input as JSON (an MCP call). Null for a shell command (its command
 * is all of it).
 */
export function inputPreview(a: Pick<ApprovalRequest, 'tool'> & { input?: unknown }): string | null {
  if (a.tool && SHELL_TOOLS.has(a.tool.toLowerCase())) return null;
  const input = a.input;
  if (input === undefined || input === null) return null;
  const o = typeof input === 'object' && !Array.isArray(input) ? (input as Record<string, unknown>) : null;
  let text: string;
  if (o && typeof o.new_string === 'string') text = `${typeof o.old_string === 'string' ? `Replace:\n${o.old_string}\n\nWith:\n` : ''}${o.new_string}`;
  else if (o && typeof o.content === 'string') text = o.content;
  else if (o && o.truncated === true && typeof o.preview === 'string') text = o.preview;
  else text = JSON.stringify(input, null, 2);
  return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX)}\n(${text.length - PREVIEW_MAX} more characters; open the request in the Inbox to read all of it)` : text;
}

/** Every decided request the console knows, newest first: blocking first, then recent, once each. */
export function decidedList(s: Partial<ApprovalsState> | null | undefined): ApprovalRequest[] {
  if (!s) return [];
  const seen = new Set<Id>();
  const out: ApprovalRequest[] = [];
  for (const a of [...(s.blocking ?? []), ...(s.recent ?? [])]) {
    if (seen.has(a.id)) continue;
    seen.add(a.id);
    out.push(a);
  }
  return out;
}

export function findDecided(s: Partial<ApprovalsState> | null | undefined, id: Id): ApprovalRequest | undefined {
  return s?.blocking?.find((a) => a.id === id) ?? s?.recent?.find((a) => a.id === id);
}

/** When the one-shot grant of an answered request runs out: the grant when the hub sent it, else 30 minutes after the resume. */
export function grantUntil(s: Partial<ApprovalsState> | null | undefined, a: ApprovalLike): number | null {
  if (!a.resume) return null;
  const g = s?.grants?.find((x) => x.id === a.resume!.grantId);
  return g?.expiresAt ?? a.resume.at + GRANT_MS;
}

export type BlockKind = 'blocked' | 'policy' | 'resuming' | 'resume_failed' | 'not_run' | 'live';

export interface BlockView {
  kind: BlockKind;
  /** failed: needs a person. working: resuming. waiting: approved, the session has to be told to continue. */
  tone: 'failed' | 'working' | 'waiting';
  label: string;
  tool: string | null;
  command: string | null;
  /** "Asked 03:16, expired 03:31 with no answer." */
  when: string;
  /** A person's note, or the policy's reason. */
  note: string | null;
  /** What happened since, for the resume phases. */
  detail: string | null;
  /** Offer Approve and resume. */
  resumable: boolean;
  /** The rule that refused it, for "Open the rule". */
  ruleId: string | null;
  /**
   * The primary action now: 'handled' leads with Mark as handled and keeps Approve and resume
   * behind a menu and its confirm (a redirect, approvalBlockLead); 'resume' leads with Approve
   * and resume; 'none' offers neither first (policy, resuming, approved while open).
   */
  lead: ApprovalBlockLead;
  /** Who redirected it with a note, for the confirm. */
  redirect: ApprovalRedirect | null;
  /** The one dim line under Mark as handled when it leads: why (redirectWhy). */
  why: string | null;
}

export interface BlockViewContext {
  machineName: string;
  /** When the one-shot grant runs out (the live case). */
  grantUntil?: number | null;
  /** The session's status now: an approval granted while it was open can be resumed once it ended. */
  sessionStatus?: Session['status'];
  /** Who started the session: one Orgus launched that is open and idle can be continued again. */
  sessionOrigin?: Session['origin'];
  /** Clock formatting; tests pass a fixed one. */
  clock?: (ts: number) => string;
  /** The signed-in person's id: their own denial reads "You" in the why line. */
  me?: string | null;
  /** How the console names a person ("the owner"): usePersonName. */
  name?: (id: string) => string;
}

/**
 * Approved while it was open ('live'), and pressing again can do more now: the session ended (it
 * restarts), or Orgus launched it and it is open but not working (the hub continues it through
 * its input, R-HL-4).
 */
export function liveCanContinue(r: Pick<ApprovalResume, 'state'> | undefined, s: Partial<Pick<Session, 'status' | 'origin'>> | undefined): boolean {
  if (r?.state !== 'live' || !s?.status) return false;
  return s.status === 'ended' || (s.origin === 'orgus' && s.status !== 'working');
}

/** What a blocked session's card, chip and timeline say about its block. */
export function blockView(b: ApprovalBlock, ctx: BlockViewContext): BlockView {
  const c = ctx.clock ?? formatClock;
  const a = b.approval;
  const at = a.createdAt;
  const decided = a.decidedAt ?? a.createdAt;
  const policy = b.outcome === 'denied' && a.decidedBy === 'policy';
  const by = a.decidedBy ?? 'Someone';
  const when =
    b.outcome === 'expired'
      ? a.decidedBy === 'machine'
        ? `Asked ${c(at)}. The machine lost contact with Orgus and gave up at ${c(decided)}.`
        : `Asked ${c(at)}, expired ${c(decided)} with no answer.`
      : policy
        ? `Asked ${c(at)}, refused by policy at ${c(decided)}.`
        : `Asked ${c(at)}, denied ${c(decided)} by ${by}.`;
  const note = b.outcome === 'denied' && a.note ? (policy ? `Why: ${a.note}` : `Note from ${by}: ${a.note}`) : null;
  const r = a.resume;
  let kind: BlockKind = policy ? 'policy' : 'blocked';
  let label = b.label;
  let detail: string | null = null;
  let tone: BlockView['tone'] = 'failed';
  if (b.phase === 'resuming' && r) {
    kind = 'resuming';
    tone = 'working';
    // The hub's own answer when it has one: it says exactly what it did (continued or restarted).
    if (r.via === 'message') {
      label = `Continuing on ${ctx.machineName}`;
      detail = r.message ?? `Approved and continued by ${r.by} at ${c(r.at)}. Orgus told the session to go ahead, and the call is allowed once.`;
    } else {
      label = `Resuming on ${ctx.machineName}`;
      detail = r.message ?? `Approved and resumed by ${r.by} at ${c(r.at)}. The session is starting again, and the call is allowed once.`;
    }
  } else if (b.phase === 'resume_failed') {
    kind = 'resume_failed';
    label = APPROVAL_BLOCK_LABEL.resume_failed;
    detail = `The last try${r ? ` at ${c(r.at)}` : ''} failed: ${r?.message ?? 'the machine did not say why.'}`;
  } else if (b.phase === 'not_run' && r) {
    kind = 'not_run';
    label = APPROVAL_BLOCK_LABEL.not_run;
    detail = `It resumed at ${c(r.at)} but ended without running the approved call. Approve and resume again, or open it in a terminal.`;
  } else if (r?.state === 'live' && ctx.sessionStatus === 'ended') {
    // Approved while it was open; it ended without continuing. Approve and resume restarts it now.
    detail = `Approved by ${r.by} at ${c(r.at)} while it was still open, but it ended before it continued. Approve and resume starts it again.`;
  } else if (r && liveCanContinue(r, { status: ctx.sessionStatus, origin: ctx.sessionOrigin })) {
    // Approved while open (before Orgus continued launched sessions), still idle: press again.
    detail = `Approved by ${r.by} at ${c(r.at)}, but it has not continued. Approve and resume continues it now.`;
  } else if (r?.state === 'live') {
    kind = 'live';
    tone = 'waiting';
    label = 'Approved: not continued yet';
    const until = ctx.grantUntil ?? r.at + GRANT_MS;
    detail = r.message ?? `Approved by ${r.by}. Tell it to continue in its terminal on ${ctx.machineName}; the call is allowed once until ${c(until)}.`;
  }
  const resumable = b.resumable && (kind === 'blocked' || kind === 'resume_failed' || kind === 'not_run');
  // A redirect leads with Mark as handled only while it simply stands (approvalBlockLead).
  const lead: ApprovalBlockLead = b.lead === 'handled' && kind === 'blocked' && resumable ? 'handled' : resumable ? 'resume' : 'none';
  return {
    kind,
    tone,
    label,
    tool: a.tool ?? null,
    command: commandOf(a as ApprovalRequest) ?? a.title,
    when,
    note,
    detail,
    resumable,
    ruleId: policy ? a.ruleId ?? null : null,
    lead,
    redirect: b.redirect ?? null,
    why: lead === 'handled' && b.redirect ? redirectWhy(b.redirect, ctx.me, ctx.name) : null,
  };
}

/** Why Approve and resume cannot be pressed here, or null when it can. */
export function resumeDisabledReason(p: { canApprove: boolean; machineName: string; online: boolean; known: boolean }): string | null {
  if (!p.known) return 'This request is no longer here. Reload the page.';
  if (!p.canApprove) return 'Viewers cannot approve. Ask an operator.';
  if (!p.online) return `${p.machineName} is offline. Start Orgus there to resume.`;
  return null;
}

export type ApprovalEventKind = 'allowed' | 'expired' | 'denied' | 'policy' | 'resumed' | 'approved' | 'resume_failed' | 'ran' | 'handled' | 'ask_open' | 'ask_answered' | 'ask_declined';

export interface ApprovalEventRow {
  key: string;
  at: number;
  kind: ApprovalEventKind;
  label: string;
  command: string | null;
  approvalId: Id;
  /** The decision row of the request that blocks the session now: it carries Approve and resume. */
  active: boolean;
  /** A question or plan passed back to the session: what it asked. */
  detail?: string;
  /** Answered in the session: each question (its header chip) and its answer. */
  answers?: { question: string; header: string; answer: string }[];
}

/**
 * A decided question or plan as a row: answered in Orgus, or passed back to the session (ADR
 * 0056) and then asked there or answered there, with who and each question's answer. Null for
 * anything else (a pending one, a tool request, a form).
 */
export function askEventRow(a: ApprovalRequest, who: (by: string) => string): ApprovalEventRow | null {
  if ((a.kind !== 'question' && a.kind !== 'plan') || a.state === 'pending') return null;
  const passed = a.state === 'expired' && a.fallback === 'session';
  const sa = a.sessionAnswer;
  // Withdrawn because the turn ended, or expired without a way back to the session: nothing to say here.
  if (a.state === 'expired' && !passed) return null;
  const asked = a.ask?.type === 'questions' ? a.ask.questions : [];
  const detail = a.ask?.type === 'questions' ? (asked.length > 1 ? `${asked[0]!.question} (+${asked.length - 1} more)` : (asked[0]?.question ?? a.title)) : a.title;
  const base = { key: `${a.id}:ask`, command: null, approvalId: a.id, active: false, detail };
  if (passed && !sa) return { ...base, at: a.decidedAt ?? a.createdAt, kind: 'ask_open', label: a.handedBack ? 'Handed back to the terminal' : 'Asked in the terminal' };
  const answeredOk = sa ? sa.outcome === 'answered' : a.state === 'allowed';
  const by = sa ? sa.by : (a.decidedBy ?? null);
  const note = sa ? sa.note : a.note;
  const given = sa ? sa.answers : a.askAnswer?.answers;
  const what = a.kind === 'plan' ? (answeredOk ? 'Plan approved' : 'Plan sent back') : answeredOk ? 'Answered' : 'Skipped';
  const answers = answeredOk && given ? asked.filter((q) => given[q.question]).map((q) => ({ question: q.question, header: q.header, answer: given[q.question]! })) : [];
  return {
    ...base,
    at: sa ? sa.at : (a.decidedAt ?? a.createdAt),
    kind: answeredOk ? 'ask_answered' : 'ask_declined',
    label: `${what}${sa ? ' in the session' : ''}${by ? ` by ${who(by)}` : ''}${note && !answeredOk ? `: ${note}` : ''}`,
    ...(answers.length ? { answers } : {}),
  };
}

/** The decided questions and plans of some sessions (a mission's), newest first, as rows. */
export function askEvents(sessionIds: readonly Id[], s: Partial<ApprovalsState> | null | undefined, who: (by: string) => string, max = 8): ApprovalEventRow[] {
  const rows: ApprovalEventRow[] = [];
  for (const a of decidedList(s)) {
    if (!sessionIds.includes(a.sessionId)) continue;
    const row = askEventRow(a, who);
    if (row) rows.push(row);
  }
  return rows.sort((x, y) => y.at - x.at).slice(0, max);
}

/**
 * The session timeline's approval events: tool requests a person allowed, decisions that stopped
 * it and what happened after, and questions and plans passed back to the session (asked there, or
 * answered there). Newest first. A request still pending is not here: the drawer's card above
 * answers it, so it shows once.
 */
export function approvalEvents(sessionId: Id, s: Partial<ApprovalsState> | null | undefined, activeId: Id | null, max = 5, who: (by: string) => string = (by) => by): ApprovalEventRow[] {
  const rows: ApprovalEventRow[] = [];
  for (const a of decidedList(s)) {
    if (a.sessionId !== sessionId) continue;
    if (a.onBehalfOf && !a.delegate) {
      rows.push({ key: `${a.id}:automatic`, at: a.decidedAt ?? a.createdAt, kind: a.state === 'allowed' ? 'allowed' : 'denied', label: `${a.state === 'allowed' ? 'Allowed' : a.state === 'expired' ? 'Expired' : 'Denied'} by ${decidedText(a, (by) => (by ? who(by) : 'someone'))}`, command: commandOf(a) ?? a.title, approvalId: a.id, active: false });
      continue;
    }
    // A tool request a person allowed: what it asked and the answer (a policy allow never asked anyone).
    if (a.state === 'allowed' && a.kind === 'tool' && a.decidedBy && !APPROVAL_SYSTEM_DECIDERS.has(a.decidedBy)) {
      const scope = a.scope === 'session' ? 'Allowed for this session' : a.scope === 'project' ? 'Allowed with a rule' : 'Allowed once';
      rows.push({ key: `${a.id}:allowed`, at: a.decidedAt ?? a.createdAt, kind: 'allowed', label: `${scope} by ${who(a.decidedBy)}`, command: commandOf(a) ?? a.title, approvalId: a.id, active: false });
      continue;
    }
    // A question or plan passed back to the session: the drawer's receipt card covers one answered in Orgus.
    if (a.state === 'expired' && a.fallback === 'session') {
      const passed = askEventRow(a, who);
      if (passed) {
        rows.push(passed);
        continue;
      }
    }
    const outcome = approvalOutcomeOf(a);
    if (!outcome) continue;
    const cmd = commandOf(a) ?? a.title;
    const policy = outcome === 'denied' && a.decidedBy === 'policy';
    const decided = a.decidedAt ?? a.createdAt;
    rows.push({
      key: `${a.id}:decided`,
      at: decided,
      kind: outcome === 'expired' ? 'expired' : policy ? 'policy' : 'denied',
      label:
        outcome === 'expired'
          ? a.decidedBy === 'machine'
            ? 'Approval expired: the machine lost contact with Orgus'
            : 'Approval expired: nobody answered'
          : policy
            ? 'Refused by policy'
            : `Denied by ${a.decidedBy ? who(a.decidedBy) : 'someone'}`,
      command: cmd,
      approvalId: a.id,
      active: a.id === activeId,
    });
    const r = a.resume;
    if (r) {
      const label =
        r.state === 'failed'
          ? `Approve and resume failed: ${r.message ?? 'the machine did not say why.'}`
          : r.state === 'live'
            ? `Approved by ${r.by}; the session was still open, so it was told nothing`
            : r.via === 'message'
              ? `Approved and continued by ${r.by}`
              : `Approved and resumed by ${r.by}`;
      rows.push({ key: `${a.id}:resume`, at: r.at, kind: r.state === 'failed' ? 'resume_failed' : r.state === 'live' ? 'approved' : 'resumed', label, command: null, approvalId: a.id, active: false });
      if (r.state === 'ran' && a.laterWorkAt) rows.push({ key: `${a.id}:ran`, at: a.laterWorkAt, kind: 'ran', label: 'Allowed once: the approved call ran', command: cmd, approvalId: a.id, active: false });
    }
    if (a.dismissedAt) {
      const label = a.dismissedNote === MISSION_CLOSED_NOTE ? `Handled: ${a.dismissedBy ?? 'someone'} closed the mission` : `Marked as handled by ${a.dismissedBy ?? 'someone'}`;
      rows.push({ key: `${a.id}:handled`, at: a.dismissedAt, kind: 'handled', label, command: null, approvalId: a.id, active: false });
    }
  }
  return rows.sort((x, y) => y.at - x.at).slice(0, max);
}

export interface BlockedItem {
  approval: ApprovalRequest;
  block: ApprovalBlock;
  session: Session | null;
}

/**
 * The blocking requests that still stop their session, newest first. A request whose session the
 * console does not know any more still shows (blocked), so it can be marked as handled.
 */
export function blockedList(s: Partial<ApprovalsState> | null | undefined, sessions: Record<Id, Session | undefined>, now: number): BlockedItem[] {
  const all = approvalsForDerive(s) ?? [];
  const out: BlockedItem[] = [];
  for (const a of s?.blocking ?? []) {
    const outcome = approvalOutcomeOf(a);
    if (!outcome || a.dismissedAt || a.laterWorkAt) continue;
    const session = sessions[a.sessionId] ?? null;
    if (session) {
      const block = approvalBlockFor(session, all, now);
      if (block && block.approval.id === a.id) out.push({ approval: a, block, session });
      continue;
    }
    const policy = outcome === 'denied' && a.decidedBy === 'policy';
    out.push({
      approval: a,
      session: null,
      block: {
        outcome,
        approval: a,
        phase: 'blocked',
        resumable: approvalResumable(a),
        label: outcome === 'expired' ? APPROVAL_BLOCK_LABEL.expired : policy ? APPROVAL_BLOCK_LABEL.policy : APPROVAL_BLOCK_LABEL.denied,
        title: approvalBlockTitle(a),
        ...approvalBlockLead(a),
      },
    });
  }
  return out.sort((x, y) => (y.approval.decidedAt ?? y.approval.createdAt) - (x.approval.decidedAt ?? x.approval.createdAt));
}

/**
 * The hub's close rule (ApprovalsApi.resolveForMission), for the demo: closing a mission marks
 * each of its blocking requests handled by whoever closed it, note "Mission closed", and takes
 * it off the blocking list. Its requests: raised for it, or by one of its sessions from that
 * session's machine. Pure.
 */
export function resolveMissionBlocks(s: ApprovalsState, m: Pick<Mission, 'id' | 'sessionIds' | 'launches'>, by: string, now: number): ApprovalsState {
  const machineOf = new Map<Id, Id | null>();
  for (const l of m.launches) if (l.sessionId) machineOf.set(l.sessionId, l.machineId);
  for (const id of m.sessionIds) if (!machineOf.has(id)) machineOf.set(id, null);
  const mine = (a: ApprovalRequest) => a.missionId === m.id || (machineOf.has(a.sessionId) && (machineOf.get(a.sessionId) == null || machineOf.get(a.sessionId) === a.machineId));
  const hit = (s.blocking ?? []).filter(mine);
  if (!hit.length) return s;
  let next = s;
  for (const a of hit) next = putDecided(next, { ...a, dismissedAt: now, dismissedBy: by, dismissedNote: MISSION_CLOSED_NOTE }, false);
  return next;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** The signed-in person's id, or null (no auth module, or not loaded yet). */
export function useMeId(): string | null {
  return useMe((s) => s.me?.actor.id ?? null);
}

/** Why this session is stopped on an approval, or null. Re-evaluated every 15 s for the resume grace. */
export function useApprovalBlock(session: (Pick<Session, 'id' | 'status' | 'startedAt' | 'lastActivity'> & Partial<Pick<Session, 'machineId'>>) | null | undefined): ApprovalBlock | null {
  const { approvals } = useApprovals();
  const now = useNow(15_000);
  return useMemo(() => (session && approvals ? approvalBlockFor(session, approvalsForDerive(approvals) ?? [], now) : null), [session, approvals, now]);
}

/** The Inbox's Blocked list: loading and error follow the approvals module. */
export function useBlockedApprovals(): { phase: ReturnType<typeof useApprovals>['phase']; items: BlockedItem[]; approvals: ApprovalsState | null } {
  const { phase, approvals } = useApprovals();
  const sessions = useOrgus((s) => s.sessions);
  const now = useNow(15_000);
  const items = useMemo(() => blockedList(approvals, sessions, now), [approvals, sessions, now]);
  return { phase, items, approvals };
}

/** One decided request by id, from blocking or recent. */
export function useDecidedApproval(id: Id | null | undefined): ApprovalRequest | undefined {
  return useOrgus((s) => (id ? findDecided(s.ext.approvals as ApprovalsState | undefined, id) : undefined));
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

function setApprovals(next: ApprovalsState): void {
  useOrgus.getState().applyExt({ ext: 'approvals', type: 'state', data: next });
}

/** Replace one decided request in blocking and recent; `keep: false` takes it off the blocking list. */
export function putDecided(cur: ApprovalsState, a: ApprovalRequest, keep: boolean, grants?: ApprovalGrant[]): ApprovalsState {
  const blocking = (cur.blocking ?? []).filter((x) => x.id !== a.id);
  return {
    ...cur,
    blocking: keep ? [a, ...blocking] : blocking,
    recent: cur.recent.some((x) => x.id === a.id) ? cur.recent.map((x) => (x.id === a.id ? a : x)) : [a, ...cur.recent].slice(0, 50),
    ...(grants ? { grants } : {}),
  };
}

const later = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** The demo's Approve and resume: the same checks and answers as the hub, against demo data. */
async function mockResume(id: Id): Promise<ApprovalResumeResponse> {
  await later(700);
  const st = useOrgus.getState();
  const cur = st.ext.approvals as ApprovalsState;
  const a = findDecided(cur, id);
  if (!a) throw new ApiError({ what: 'The session was not resumed.', why: 'That request is not here any more.', next: 'Reload the page.' }, 404);
  const m = st.machines[a.machineId];
  const name = m?.name ?? 'its machine';
  if (!m?.online) throw new ApiError({ what: 'The session was not resumed.', why: `${name} is offline, so Orgus cannot reach the session.`, next: `Start Orgus on ${name}; the request stays here and Approve and resume works once it is back.` }, 409);
  const s = st.sessions[a.sessionId];
  if (!s) throw new ApiError({ what: 'The session was not resumed.', why: 'Orgus no longer sees that session.', next: 'Start a new session from the mission.' }, 409);
  const now = Date.now();
  const grant: ApprovalGrant = { id: newPrefixedId('grt', now), approvalId: a.id, machineId: a.machineId, sessionId: a.sessionId, tool: a.tool ?? '', fingerprint: a.fingerprint ?? 'demo', ...(a.subject ? { subject: a.subject } : {}), by: 'you', at: now, expiresAt: now + GRANT_MS };
  const until = formatClock(grant.expiresAt);
  // Launched here and open but idle: continued through its input (the hub's 'message' path).
  const viaMessage = s.status !== 'ended' && s.status !== 'working' && s.origin === 'orgus';
  const live = s.status !== 'ended' && !viaMessage;
  const message = viaMessage
    ? `Approved and continued: ${a.title}. Orgus told it to go ahead on ${name}; the call is allowed once until ${until}.`
    : live
      ? `Approved. The session is still open in its terminal on ${name}, so it was not restarted. Tell it to continue; the call is allowed once until ${until}.`
      : `Approved and resumed: ${a.title}. It continues on ${name}.`;
  const approval: ApprovalRequest = { ...a, resume: { by: 'you', at: now, from: 'this browser (demo)', grantId: grant.id, state: live ? 'live' : 'sent', ...(live ? {} : { via: viaMessage ? 'message' : 'restart' }), message } };
  setApprovals(putDecided(cur, approval, true, [...(cur.grants ?? []), grant]));
  if (live) return { approval, grant, resumed: false, message };
  // The machine starts the same session again; a few seconds later it makes the call and the grant allows it.
  st.applyPatch({ at: now, upserts: { sessions: [{ ...s, status: 'working', lastActivity: now, pid: 40_000 + (now % 9_000), lastText: `Approved by you. Running ${commandOf(a) ?? a.title} now.` }] }, removes: {} });
  setTimeout(() => {
    const st2 = useOrgus.getState();
    const cur2 = st2.ext.approvals as ApprovalsState | undefined;
    const a2 = cur2 && findDecided(cur2, id);
    if (!cur2 || !a2?.resume || a2.resume.grantId !== grant.id) return;
    const t = Date.now();
    setApprovals(putDecided(cur2, { ...a2, laterWorkAt: t, resume: { ...a2.resume, state: 'ran' } }, false, (cur2.grants ?? []).filter((g) => g.id !== grant.id)));
  }, 6_000);
  return { approval, grant, resumed: true, message };
}

/** Approve and resume: continue the same session, and let the call it asked for run once (R-HL-4). */
export async function resumeApproval(id: Id, note?: string): Promise<ApprovalResumeResponse> {
  const st = useOrgus.getState();
  if (st.mode === 'mock' && st.ext.approvals) return mockResume(id);
  const res = await apiPost<ApprovalResumeResponse>(`/api/approvals/${encodeURIComponent(id)}/resume`, note ? { note } : {});
  // Show it at once; the hub's broadcast confirms it a moment later.
  const cur = useOrgus.getState().ext.approvals as ApprovalsState | undefined;
  if (cur && res.approval) setApprovals(putDecided(cur, res.approval, !res.approval.laterWorkAt && !res.approval.dismissedAt));
  return res;
}

/** Mark as handled: the request no longer blocks its session or mission. */
export async function dismissApproval(id: Id, note?: string): Promise<ApprovalRequest> {
  const st = useOrgus.getState();
  const cur = st.ext.approvals as ApprovalsState | undefined;
  if (st.mode === 'mock' && cur) {
    await later(350);
    const a = findDecided(cur, id);
    if (!a) throw new ApiError({ what: 'It was not marked as handled.', why: 'That request is not here any more.', next: 'Reload the page.' }, 404);
    const done: ApprovalRequest = { ...a, dismissedAt: Date.now(), dismissedBy: 'you' };
    setApprovals(putDecided(cur, done, false));
    return done;
  }
  const res = await apiPost<{ approval: ApprovalRequest }>(`/api/approvals/${encodeURIComponent(id)}/dismiss`, note ? { note } : {});
  const now = useOrgus.getState().ext.approvals as ApprovalsState | undefined;
  if (now) setApprovals(putDecided(now, res.approval, false));
  return res.approval;
}
